import {
  DataFilterRule, DataFilterValue, DataFilterValues, isRule,
} from './data-filter.component';

/**
 * Reads out of a row the one value a filter column is about.
 *
 * A page owns these because only the page knows its own row shape - a
 * territory's rep is `row.territory.assignedUserId`, and nothing generic could
 * guess that.
 */
export type DataFilterReader<T> = (row: T) => DataFilterValue;

/**
 * Answers a filter bar against rows already in the browser.
 *
 * WHY THIS EXISTS. The filter bar is shared, and so is the grammar behind it -
 * `field:op:value`, four kinds of column, the comparisons each kind admits.
 * But until now the only thing that could ANSWER a rule was the database:
 * BRDB.Repository.Filtering.TableFilter parses one and CustomerLogic and
 * LeadWorkItemRepository turn it into SQL. A page whose rows are already
 * loaded - the territory roster is a company's whole list, fetched once - had
 * no way to use the bar at all, and would otherwise have grown its own
 * hand-written comparisons beside it.
 *
 * Two implementations of one grammar is how they drift, so this one is written
 * to answer the way the SQL does, and says where it does so:
 *
 *  - Rules AND. Each narrows what the last one left, which is what "and" means
 *    on a filter bar and the only reading that lets somebody add a rule and
 *    watch the count go down.
 *  - A missing value never matches ANY comparison. That is SQL's own answer
 *    for NULL: a territory with no rep is not assigned to somebody after all,
 *    it is not assigned.
 *  - Text compares case-insensitively, because ILike does.
 *
 * A column with no reader is skipped rather than failing the row. That is the
 * dangerous default of the two - a filter that quietly matches everything - so
 * it is not left to chance: the page that declares the fields is expected to
 * declare a reader for each, and a test holds the two lists together.
 */
export function matchesFilters<T>(
  row: T,
  values: DataFilterValues,
  readers: Readonly<Record<string, DataFilterReader<T>>>,
): boolean {
  for (const [field, held] of Object.entries(values ?? {})) {
    if (held === null || held === undefined || held === '') continue;
    const read = readers[field];
    if (!read) continue;
    if (!matchesOne(read(row), held)) return false;
  }
  return true;
}

/** One column, one comparison. */
function matchesOne(
  actual: DataFilterValue,
  held: DataFilterValue | DataFilterRule,
): boolean {
  // A choice column carries a bare value and means "is". Both sides come from
  // the same option list, so identity is the whole comparison.
  if (!isRule(held)) return actual === held;

  if (actual === null || actual === undefined) return false;

  switch (held.op) {
    case 'contains':
      return String(actual).toLowerCase().includes(String(held.value).toLowerCase());

    // The one comparison that has to ask what KIND of value it is holding: a
    // number is equal numerically, and text is equal the way ILike says it is.
    case 'eq':
      return typeof held.value === 'number'
        ? Number(actual) === held.value
        : String(actual).toLowerCase() === String(held.value).toLowerCase();

    case 'gte': return Number(actual) >= Number(held.value);
    case 'lte': return Number(actual) <= Number(held.value);

    // Dates arrive as the ISO day the date input produced. Compared as days,
    // not instants: "on or before the 21st" has to include the 21st, which is
    // the same half-open boundary the SQL side spells out.
    case 'after': return day(actual) >= day(held.value);
    case 'before': return day(actual) <= day(held.value);

    default: return true;
  }
}

/** The calendar day of a value, as a sortable string. */
function day(value: DataFilterValue): string {
  const text = String(value);
  // Already an ISO day or an ISO timestamp: the first ten characters are the
  // day, and comparing them as text is comparing them as dates.
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime())
    ? text
    : parsed.toISOString().slice(0, 10);
}
